import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Star, Trash2, Plus } from 'lucide-react';
import { validarCodigo, type TipoCodigo } from '@profitlens/dominio';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro } from '../componentes/Estados';
import { Campo, CampoInteiro, CampoQuantidade, CampoReais, CampoTermos, Texto } from '../componentes/Campos';
import { Procedencia } from '../componentes/Procedencia';
import { useAuth } from '../contextos/AuthContext';
import { useUsuarios } from '../servicos/usuarios';
import { useCategorias } from '../servicos/categorias';
import {
  adicionarCodigo,
  produtoVazio,
  removerCodigo,
  salvarProduto,
  tornarPrincipal,
  useCodigos,
  useDuplicidades,
  useProdutos,
  type RascunhoProduto,
} from '../servicos/produtos';

const ABAS = ['Dados', 'Códigos', 'Medidas'] as const;
type Aba = (typeof ABAS)[number];

const TIPOS: TipoCodigo[] = ['GTIN', 'EAN', 'UPC', 'ISBN', 'OUTRO'];
const UNIDADES = ['UN', 'CX', 'PC', 'KG', 'G', 'L', 'ML', 'M', 'CM', 'PAR', 'KIT'];

export function MercadoriaFicha() {
  const { id } = useParams<{ id: string }>();
  const novo = id === 'nova';
  const produtoId = novo ? null : (id ?? null);

  const navegar = useNavigate();
  const { perfil, podeEscrever } = useAuth();
  const { produtos, carregando } = useProdutos();
  const { usuarios } = useUsuarios();
  const codigos = useCodigos(produtoId);
  const { categorias } = useCategorias();

  const [aba, setAba] = useState<Aba>('Dados');
  const [rascunho, setRascunho] = useState<RascunhoProduto>(produtoVazio());
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const existente = produtos.find((p) => p.id === produtoId);

  useEffect(() => {
    if (!existente) return;
    const { id: _ignorado, criadoEm, criadoPor, atualizadoEm, atualizadoPor, ...resto } = existente;
    setRascunho(resto);
  }, [existente?.id]);

  const valoresCodigos = useMemo(() => codigos.map((c) => c.valor), [codigos]);
  const duplicidades = useDuplicidades(
    { nome: rascunho.nome, marca: rascunho.marca },
    valoresCodigos,
    produtoId,
  );

  const nomes = useMemo(
    () => Object.fromEntries(usuarios.map((u) => [u.id, u.nome])),
    [usuarios],
  );

  if (!novo && carregando) return <Carregando />;
  if (!novo && !existente && !carregando) {
    return <Erro titulo="Produto não encontrado" detalhe="Ele pode ter sido removido." />;
  }

  const editar = <K extends keyof RascunhoProduto>(campo: K, valor: RascunhoProduto[K]) =>
    setRascunho((r) => ({ ...r, [campo]: valor }));

  function escolherCategoria(novoId: string | null) {
    setRascunho((r) => ({ ...r, categoriaId: novoId }));
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    setErro(null);
    setSalvando(true);
    try {
      const novoId = await salvarProduto(produtoId, rascunho, perfil.id);
      if (novo) navegar(`/mercadorias/${novoId}`, { replace: true });
    } catch (ex) {
      setErro((ex as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={salvar} className="flex flex-col gap-5">
      <Cabecalho
        titulo={novo ? 'Nova mercadoria' : rascunho.nome || 'Mercadoria'}
        descricao={novo ? 'O cadastro é o ponto de partida da cotação e da pesquisa de preço.' : undefined}
        acoes={
          <div className="flex gap-2">
            <Link
              to="/mercadorias"
              className="inline-flex items-center gap-1.5 rounded border border-regua-2 px-3 py-1.5 text-sm text-tinta-2 hover:bg-superficie-2"
            >
              <ArrowLeft className="size-4" aria-hidden />
              Voltar
            </Link>
            {podeEscrever && (
              <button
                type="submit"
                disabled={salvando || rascunho.nome.trim() === ''}
                className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
              >
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
            )}
          </div>
        }
      />

      {duplicidades.length > 0 && (
        <div className="flex flex-col gap-2 rounded-md border-l-3 border-c-res bg-c-res/10 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-c-res">
            <AlertTriangle className="size-4" aria-hidden />
            Possível duplicidade
          </p>
          <ul className="flex flex-col gap-1 text-sm">
            {duplicidades.map((d) => (
              <li key={d.produtoId}>
                <Link to={`/mercadorias/${d.produtoId}`} className="font-medium underline-offset-2 hover:underline">
                  {d.nome}
                </Link>
                <span className="text-tinta-2"> — {d.detalhe}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-tinta-3">
            Isto é um alerta, não um bloqueio: quem sabe se são o mesmo produto é você.
          </p>
        </div>
      )}

      {erro && <Erro titulo="Não foi possível salvar" detalhe={erro} />}

      <div className="flex gap-1 border-b border-regua">
        {ABAS.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            aria-current={aba === a}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
              aba === a
                ? 'border-marca font-semibold text-marca'
                : 'border-transparent text-tinta-2 hover:text-tinta'
            }`}
          >
            {a}
            {a === 'Códigos' && codigos.length > 0 && (
              <span className="ml-1.5 font-mono text-[0.65rem] text-tinta-3">{codigos.length}</span>
            )}
          </button>
        ))}
      </div>

      {aba === 'Dados' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Texto rotulo="Nome *" valor={rascunho.nome} aoMudar={(v) => editar('nome', v ?? '')} obrigatorio desabilitado={!podeEscrever} />
          </div>
          <Texto rotulo="Marca" valor={rascunho.marca} aoMudar={(v) => editar('marca', v)} desabilitado={!podeEscrever} dica="Usada na detecção de duplicidade e na correspondência de anúncio." />
          <Texto rotulo="Modelo" valor={rascunho.modelo} aoMudar={(v) => editar('modelo', v)} desabilitado={!podeEscrever} />
          {/*
            Categoria deixou de ser texto livre: e ela que casa o produto com a
            comissao do perfil de custo, e "Audio" digitado com acento diferente
            viraria outra categoria -- a comissao sumiria sem ninguem perceber.
          */}
          <Campo
            rotulo="Categoria"
            dica={
              categorias.length === 0
                ? 'Nenhuma cadastrada. O administrador cria em Configurações › Categorias.'
                : 'Define a comissão do marketplace na análise.'
            }
          >
            <select
              value={rascunho.categoriaId ?? ''}
              disabled={!podeEscrever || categorias.length === 0}
              onChange={(e) => escolherCategoria(e.target.value || null)}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            >
              <option value="">Sem categoria</option>
              {categorias
                .filter((c) => c.ativo || c.id === rascunho.categoriaId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                    {c.ativo ? '' : ' (inativa)'}
                  </option>
                ))}
            </select>
          </Campo>
          <Texto rotulo="Código interno" valor={rascunho.codigoInterno} aoMudar={(v) => editar('codigoInterno', v)} desabilitado={!podeEscrever} />
          <Campo rotulo="Unidade de medida">
            <select
              value={rascunho.unidadeMedida}
              disabled={!podeEscrever}
              onChange={(e) => editar('unidadeMedida', e.target.value)}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            >
              {UNIDADES.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </Campo>
          <CampoQuantidade
            rotulo="Quantidade por embalagem *"
            milli={rascunho.qtdPorEmbalagemMilli}
            aoMudar={(v) => editar('qtdPorEmbalagemMilli', v ?? 1000)}
            desabilitado={!podeEscrever}
            dica="Divisor da normalização de preço. Precisa ser maior que zero."
          />
          <CampoReais rotulo="Preço-alvo de compra" cents={rascunho.precoAlvoCompraCents} aoMudar={(v) => editar('precoAlvoCompraCents', v)} desabilitado={!podeEscrever} />
          <CampoReais rotulo="Preço mínimo de venda" cents={rascunho.precoMinimoVendaCents} aoMudar={(v) => editar('precoMinimoVendaCents', v)} desabilitado={!podeEscrever} />
          <div className="sm:col-span-2">
            <CampoTermos rotulo="Palavras-chave" termos={rascunho.palavrasChave} aoMudar={(v) => editar('palavrasChave', v)} desabilitado={!podeEscrever} dica="Alimentam a busca de mercado quando o código universal não encontra nada." />
          </div>
          <div className="sm:col-span-2">
            <CampoTermos rotulo="Sinônimos" termos={rascunho.sinonimos} aoMudar={(v) => editar('sinonimos', v)} desabilitado={!podeEscrever} />
          </div>
          <div className="sm:col-span-2">
            <Texto rotulo="Descrição" valor={rascunho.descricao} aoMudar={(v) => editar('descricao', v)} desabilitado={!podeEscrever} />
          </div>
          <div className="sm:col-span-2">
            <Texto rotulo="Observações" valor={rascunho.observacoes} aoMudar={(v) => editar('observacoes', v)} desabilitado={!podeEscrever} />
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={rascunho.ativo}
              disabled={!podeEscrever}
              onChange={(e) => editar('ativo', e.target.checked)}
              className="size-4 accent-[var(--color-marca)]"
            />
            Ativo
            <span className="text-xs text-tinta-3">
              Produto inativo não entra em novas análises, mas permanece no histórico das antigas.
            </span>
          </label>
        </div>
      )}

      {aba === 'Códigos' && (
        <AbaCodigos produtoId={produtoId} codigos={codigos} podeEscrever={podeEscrever} />
      )}

      {aba === 'Medidas' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <CampoInteiro rotulo="Peso" valor={rascunho.pesoGramas} aoMudar={(v) => editar('pesoGramas', v)} sufixo="g" desabilitado={!podeEscrever} />
          <CampoInteiro rotulo="Comprimento" valor={rascunho.comprimentoMm} aoMudar={(v) => editar('comprimentoMm', v)} sufixo="mm" desabilitado={!podeEscrever} />
          <CampoInteiro rotulo="Largura" valor={rascunho.larguraMm} aoMudar={(v) => editar('larguraMm', v)} sufixo="mm" desabilitado={!podeEscrever} />
          <CampoInteiro rotulo="Altura" valor={rascunho.alturaMm} aoMudar={(v) => editar('alturaMm', v)} sufixo="mm" desabilitado={!podeEscrever} />
          <div className="sm:col-span-2">
            <Texto
              rotulo="NCM"
              valor={rascunho.ncm}
              aoMudar={(v) => editar('ncm', v)}
              desabilitado={!podeEscrever}
              dica="Classificação fiscal, quando aplicável. Usada na importação."
            />
          </div>
        </div>
      )}

      {existente && (
        <Procedencia
          criadoPor={existente.criadoPor}
          criadoEm={existente.criadoEm}
          atualizadoPor={existente.atualizadoPor}
          atualizadoEm={existente.atualizadoEm}
          entidade="produtos"
          entidadeId={existente.id}
          nomes={nomes}
        />
      )}
    </form>
  );
}

function AbaCodigos({
  produtoId,
  codigos,
  podeEscrever,
}: {
  produtoId: string | null;
  codigos: { id: string; tipo: string; valor: string; principal: boolean }[];
  podeEscrever: boolean;
}) {
  const [tipo, setTipo] = useState<TipoCodigo>('EAN');
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  if (!produtoId) {
    return (
      <p className="rounded-md border border-dashed border-regua-2 bg-superficie px-4 py-6 text-center text-sm text-tinta-2">
        Salve o produto primeiro. Os códigos precisam de um produto existente para se vincular.
      </p>
    );
  }

  async function adicionar() {
    setErro(null);
    const r = validarCodigo(tipo, valor);
    if (!r.valido) {
      setErro(r.motivo ?? 'Código inválido');
      return;
    }
    try {
      await adicionarCodigo(produtoId!, tipo, r.normalizado!, codigos.length === 0);
      setValor('');
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-tinta-2">
        Um produto pode ter vários códigos; no máximo um é o principal. O dígito verificador é conferido
        na hora — é por este campo que a busca de mercado encontra o anúncio certo.
      </p>

      {podeEscrever && (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-regua-2 bg-superficie p-3">
          <Campo rotulo="Tipo">
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoCodigo)}
              className="rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
            >
              {TIPOS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Campo>
          <div className="min-w-52 flex-1">
            <Campo rotulo="Código" erro={erro}>
              <input
                type="text"
                value={valor}
                onChange={(e) => {
                  setValor(e.target.value);
                  setErro(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void adicionar();
                  }
                }}
                placeholder="7891234560123"
                className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 font-mono text-sm outline-none focus:border-marca"
              />
            </Campo>
          </div>
          <button
            type="button"
            onClick={() => void adicionar()}
            className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden />
            Adicionar
          </button>
        </div>
      )}

      {codigos.length === 0 ? (
        <p className="rounded-md border border-dashed border-regua-2 bg-superficie px-4 py-6 text-center text-sm text-tinta-2">
          Nenhum código cadastrado. Sem ele, a busca de mercado depende de nome e marca — bem menos preciso.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {codigos.map((c) => (
            <li
              key={c.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-regua-2 bg-superficie px-3 py-2"
            >
              <span className="rounded bg-marca/10 px-1.5 py-0.5 font-mono text-[0.6rem] tracking-wider text-marca uppercase">
                {c.tipo}
              </span>
              <span className="font-mono text-sm">{c.valor}</span>
              {c.principal ? (
                <span className="inline-flex items-center gap-1 text-xs text-marca">
                  <Star className="size-3 fill-current" aria-hidden />
                  principal
                </span>
              ) : (
                podeEscrever && (
                  <button
                    type="button"
                    onClick={() => void tornarPrincipal(produtoId, c.id)}
                    className="inline-flex items-center gap-1 text-xs text-tinta-3 hover:text-marca"
                  >
                    <Star className="size-3" aria-hidden />
                    tornar principal
                  </button>
                )
              )}
              {podeEscrever && (
                <button
                  type="button"
                  onClick={() => void removerCodigo(c.id)}
                  aria-label={`Remover ${c.valor}`}
                  className="ml-auto text-tinta-3 hover:text-pend"
                >
                  <Trash2 className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
